import React, { useCallback, useState } from "react";
import { AllocationMatrixBreakdownItem, EAllocationMatrixBreakdownPrductType, AllocationMatrixGuest } from "services/BookingManagerApi/types/AllocationsResponse";
import Checkbox from "ui/Checkbox";
import FluidButton from "ui/FluidButton";
import { HidingTextTooltip } from "ui/Tooltip/HidingTextTooltip";
import { AllocationMap } from "./BookingManagerGuestAllocation";
import { createAllocationKey } from "./utils";
import classNames from "classnames";

interface Props {
  allocations: AllocationMap;
  setAllocations: React.Dispatch<React.SetStateAction<AllocationMap>>
  guests: AllocationMatrixGuest[];
  products: AllocationMatrixBreakdownItem[];
  handlePatchAllocationMatrix: () => void;
  isPatchAllocationPending: boolean;
  isPatchAllocationDisabled: boolean;
  disabled: boolean;
}

const ProductAllocationMatrix: React.FC<Props> = ({ guests, products, allocations, setAllocations, handlePatchAllocationMatrix, isPatchAllocationPending, isPatchAllocationDisabled, disabled }) => {

  const toggleAllocation = useCallback((productId: string, guestId: string) => {
    const key = createAllocationKey(productId, guestId);
    setAllocations(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  const assignAllToGuest = useCallback((guestId: string) => {
    setAllocations(prev => {
      const updated = { ...prev };
      products.forEach(product => {
        const key = createAllocationKey(product.uuid, guestId);
        updated[key] = true;
      });
      return updated;
    });
  }, [products]);

  const unassignAllFromGuest = useCallback((guestId: string) => {
    setAllocations(prev => {
      const updated = { ...prev };
      products.forEach(product => {
        const key = createAllocationKey(product.uuid, guestId);
        updated[key] = false;
      });
      return updated;
    });
  }, [products]);

  const assignAllToProduct = useCallback((productId: string) => {
    setAllocations(prev => {
      const updated = { ...prev };
      guests.forEach(guest => {
        const key = createAllocationKey(productId, guest.uuid);
        updated[key] = true;
      });
      return updated;
    });
  }, [guests]);

  const unassignAllFromProduct = useCallback((productId: string) => {
    setAllocations(prev => {
      const updated = { ...prev };
      guests.forEach(guest => {
        const key = createAllocationKey(productId, guest.uuid);
        updated[key] = false;
      });
      return updated;
    });
  }, [guests]);

  const countAssignedGuests = useCallback(
    (productId: string) =>
      guests.reduce(
        (acc, guest) => (allocations[createAllocationKey(productId, guest.uuid)] ? acc + 1 : acc),
        0
      ),
    [allocations, guests]
  );

  const handleAllocateAll = useCallback(() => {
    setAllocations((preValues) => {
      const updated: AllocationMap = {};
      products.forEach(product => {
        guests.forEach(guest => {
          const key = createAllocationKey(product.uuid, guest.uuid)
          updated[key] = true;
        });
      });
      return { ...preValues, ...updated };
    });
  }, [guests, products]);

  const handleClearAll = useCallback(() => {
    setAllocations((preValues) => {
      const updated: AllocationMap = {};
      guests.forEach(guest => {
        products.forEach(product => {
          const key = createAllocationKey(product.uuid, guest.uuid);
          updated[key] = false;
        });
      });
      return { ...preValues, ...updated };
    });
  }, [guests, products]);

  return (
    <>
      <div className={classNames("overflow-auto w-[1072px] max-h-[590px] font-pt-sans font-normal", { 'opacity-50': isPatchAllocationPending })}>
        <table className="table-auto w-full min-w-fit border-collapse">
          <thead className="sticky top-0 z-20 bg-white-hard">
            <tr className="bg-white-hard border-b border-gray-20 flex">
              <th className="sticky left-0 z-[1] bg-white-hard w-[250px] p-[10px] text-left text-gray-100 text-12px border border-gray-20 font-normal content-center">
                <div>PRODUCTS TO ALLOCATE ({products.length})</div>
              </th>
              {guests.map(guest => (
                <th key={guest.uuid} className="flex-1 min-w-[93px] p-4 text-center font-medium border border-gray-20 flex flex-col justify-end">
                  <div className="flex-1 flex flex-col justify-between">
                    <HidingTextTooltip
                      tooltipContent={guest.name}
                      position="bottom-right"
                      elementClassname="justify-center"
                      tooltipContentClassname="py-[5px] px-[8px]"
                    >
                      <div className="text-[15px] text-black line-clamp-2 text-ellipsis">{guest.name}</div>
                    </HidingTextTooltip>
                    <div className="text-[13px] text-gray-100">
                      {guest.age && `Age ${guest.age}`} {guest.isLeadGuest && <span className="text-status-enquiry text-[13px] italic">Lead</span>}
                    </div>
                  </div>
                  <div className={classNames("space-x-[10px]", { "hidden": disabled })}>
                    <button
                      className={classNames("p-0 text-[13px] text-brown-prime underline bg-[inherit] font-pt-sans", { "cursor-pointer": !isPatchAllocationPending })}
                      onClick={() => !isPatchAllocationPending && assignAllToGuest(guest.uuid)}>
                      All
                    </button>
                    <button
                      className={classNames("p-0 text-[13px] text-brown-prime underline bg-[inherit] font-pt-sans", { "cursor-pointer": !isPatchAllocationPending })}
                      onClick={() => !isPatchAllocationPending && unassignAllFromGuest(guest.uuid)}>
                      None
                    </button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {products.map((product) => {
              return (
                <tr className="border-b border-gray-20 flex" key={product.uuid}>
                  <td className="sticky left-0 z-[1] bg-white w-[250px] p-[10px] align-top text-sm border border-gray-20 flex flex-col gap-[5px]">
                    <div className="font-normal text-[12px] text-gray-100">{product.productType === EAllocationMatrixBreakdownPrductType.Bespoke ? 'Activity' : product.productType}</div>
                    {product.hotelName && (
                      <HidingTextTooltip
                        tooltipContent={product.hotelName}
                        position="bottom"
                        elementClassname="justify-start"
                        tooltipContentClassname="py-[5px] px-[8px]"
                        usePortal
                      >
                        <div className="text-[15px] line-clamp-2 font-bold">{product.hotelName}</div>
                      </HidingTextTooltip>
                    )}
                    <HidingTextTooltip
                      tooltipContent={product.name}
                      position="bottom"
                      elementClassname="justify-start"
                      tooltipContentClassname="py-[5px] px-[8px]"
                      usePortal
                    >
                      <div className="text-[15px] font-normal">{product.name}</div>
                    </HidingTextTooltip>
                    <div className="text-gray-100 font-[13px]">
                      {countAssignedGuests(product.uuid)} guests assigned
                    </div>
                    <div className={classNames("space-x-[10px]", { "hidden": disabled })}>
                      <button
                        onClick={e => {
                          e.preventDefault();
                          !isPatchAllocationPending && assignAllToProduct(product.uuid);
                        }}
                        className={classNames("p-0 text-[13px] text-brown-prime underline bg-[inherit] font-pt-sans", { "cursor-pointer": !isPatchAllocationPending })}
                      >
                        All
                      </button>
                      <button
                        onClick={e => {
                          e.preventDefault();
                          !isPatchAllocationPending && unassignAllFromProduct(product.uuid);
                        }}
                        className={classNames("p-0 text-[13px] text-brown-prime underline bg-[inherit] font-pt-sans", { "cursor-pointer": !isPatchAllocationPending })}
                      >
                        None
                      </button>
                    </div>
                  </td>
                  {guests.map(guest => {
                    const key = createAllocationKey(product.uuid, guest.uuid)
                    return (
                      <td
                        key={guest.uuid}
                        className={classNames("flex-1 min-w-[93px] p-2 text-center align-middle border border-gray-20 content-center", { "opacity-50": disabled })}
                      >
                        <Checkbox disabled={disabled} checked={allocations[key] || false} onChange={() => toggleAllocation(product.uuid, guest.uuid)} wrapperClassName="inline-flex" />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className={classNames("flex gap-[10px] mt-[20px]", { "hidden": disabled })}>
        <FluidButton type="secondary" className="min-w-fit" onClick={handleClearAll} disabled={isPatchAllocationPending}>Clear All</FluidButton>
        <FluidButton type="secondary" className="min-w-fit" onClick={handleAllocateAll} disabled={isPatchAllocationPending}>Allocate All</FluidButton>
        <FluidButton type="primary" className="min-w-fit" onClick={handlePatchAllocationMatrix} isLoading={isPatchAllocationPending} disabled={isPatchAllocationDisabled}>Save</FluidButton>
      </div>
    </>
  );
};

export default ProductAllocationMatrix;
